import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, useFormikContext, FormikHelpers, Field } from "formik";
import * as Yup from "yup";
import { Typography, Alert, Modal, Paper } from "@mui/material";
import { Tooltip } from "@mui/material";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete-bin-line.svg";
import { ReactComponent as InformationIcon } from "../../assets/images/information-line.svg";
import { ReactComponent as EditIcon } from "../../assets/images/edit-2-line.svg";
import { ReactComponent as EyeIcon } from "../../assets/images/eye-line.svg";
import { ReactComponent as DownloadIcon } from "../../assets/images/download-line.svg";
import Breadcrumb from "../../components/common/Breadcrumb";
import hubStyles from "../create/Hub.module.css";
import { FileInput } from "../../components/common/FileInput";
import { TextField, TextAreaField, DropdownField } from "../../components/common/FormFields";
import FormGroup from "../../components/common/FormGroup";
import { uploadFileToS3Complete } from "../../api/s3";
import {
    createBot,
    listBots,
    updateBot,
    deleteBot,
    updateWebchatConfiguration,
    createOrUpdateBotWithWebchat,
    deleteFile,
    uploadFileFromUrl,
    listFilesByKnowledgeBase,
    Bot,
    BotCreateRequest,
    WebchatConfiguration,
    BotpressFile,
    getBot,
} from "../../api/botpress";
import ButtonComponent from "../../components/ui/Button";
import { useNotification } from "../../context/NotificationContext";
import DeleteModal from "../../components/modals/DeleteModal";
import { SketchPicker, ColorResult } from "react-color";
import { IMAGE_FORMAT } from "../../utils/image";
import ImagePreview from "../../components/images/ImagePreview";
import { useAddonAccess } from "../../hooks/useAddonAccess";
import AccessDenied from "../../components/common/AccessDenied";
import pageHeader from "../../components/layouts/PageHeader.module.css";
import studio from "../../components/modals/StudioModal.module.css";
import stepStyles from "../track/create/createStep.module.css";

// Supported file formats for chatbot training files
// Based on Botpress documentation: PDF, HTML, Text, Markdown
const CHATBOT_TRAINING_FILE_FORMATS: Record<string, string[]> = {
    "application/pdf": [".pdf"],
    "text/html": [".html"],
    "text/plain": [".txt"],
    "text/markdown": [".md"],
};

// Maximum file size for chatbot training files (10MB)
const CHATBOT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

interface ChatbotFormValues {
    name: string;
    description: string;
    logo: string | undefined;
    color: string;
    themeMode: "light" | "dark";
    phoneNumber: string;
    emailAddress: string;
    websiteUrl: string;
    files: File[];
}

const ChatbotForm = ({
    onClose,
    isEdit = false,
    botFiles = [],
    isLoadingFiles = false,
    onDeleteFile,
    onLoadBotFiles,
    onDownloadFile,
}: {
    onClose: () => void;
    isEdit?: boolean;
    originalBot?: Bot | null;
    botFiles?: BotpressFile[];
    isLoadingFiles?: boolean;
    onDeleteFile?: (fileId: string) => Promise<void>;
    onLoadBotFiles?: (botId: string) => Promise<void>;
    onDownloadFile?: (file: BotpressFile) => void;
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { values, setFieldValue, errors, touched, isSubmitting, submitForm } = useFormikContext<ChatbotFormValues>();
    const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);

    const handleLogoFileSelected = (files: File | File[]) => {
        const file = Array.isArray(files) ? files[0] : files;
        if (file) {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No authentication token found");
                return;
            }

            uploadFileToS3Complete(
                {
                    file,
                    isPublic: true, // Public files for general uploads
                    isFolder: false, // No folder structure for general uploads
                },
                token,
            )
                .then((result) => {
                    setFieldValue("logo", result);
                })
                .catch(console.error);
        }
    };

    const handleFilesSelected = (files: File | File[]) => {
        const fileArray = Array.isArray(files) ? files : [files];
        setFieldValue("files", [...values.files, ...fileArray]);
    };

    const handleColorChange = (selectedColor: ColorResult) => {
        setFieldValue("color", selectedColor.hex);
    };

    const removeFile = (index: number) => {
        const newFiles = values.files.filter((_, i) => i !== index);
        setFieldValue("files", newFiles);
    };

    return (
        <Form>
            <div className="hub-form-container">
                <h2 className={studio.dialogTitle}>{isEdit ? t("editChatbot") : t("createChatbot")}</h2>

                {/* Name */}
                <FormGroup
                    title={t("name")}
                    subtitle={t("nameDescription")}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                >
                    <Field name="name" component={TextField} placeholder={t("enterName")} />
                    {touched.name && errors.name && (
                        <Alert severity="error" className="form-error-alert">
                            {errors.name}
                        </Alert>
                    )}
                </FormGroup>

                {/* Description */}
                <FormGroup
                    title={t("description")}
                    subtitle={t("descriptionHelp")}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                >
                    <Field name="description" component={TextAreaField} placeholder={t("enterDescription")} rows={3} />
                    {touched.description && errors.description && (
                        <Alert severity="error" className="form-error-alert">
                            {errors.description}
                        </Alert>
                    )}
                </FormGroup>

                {/* Logo */}
                <FormGroup
                    title={t("logo")}
                    subtitle={t("logoDescription")}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                >
                    <div className={`${stepStyles.fileUpload} form-file-upload-constrained`}>
                        <FileInput
                            onFileSelected={handleLogoFileSelected}
                            showButton={true}
                            buttonVariant="outline"
                            placeholder={t("dragAndDropLogoHereOrClickToSelect")}
                            acceptedFileTypes={IMAGE_FORMAT}
                        />
                    </div>

                    {values.logo && (
                        <div className={hubStyles.logoPreviewContainer}>
                            <Typography variant="subtitle2" className={hubStyles.previewLabel}>
                                {t("logoPreview")}:
                            </Typography>
                            <ImagePreview
                                key={values.logo}
                                id={values.logo}
                                photo={values.logo}
                                onDelete={() => setFieldValue("logo", "")}
                            />
                        </div>
                    )}
                </FormGroup>

                {/* Color */}
                <FormGroup
                    title={t("color")}
                    subtitle={t("colorDescription")}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                >
                    <div className={hubStyles.colorInputContainer}>
                        <Field
                            name="color"
                            component={TextField}
                            placeholder={t("enterColorCode")}
                            className={hubStyles.colorInputField}
                        />
                        <div
                            className="color-preview color-picker-trigger"
                            style={{ backgroundColor: values.color || "#000000" }}
                            onClick={() => setIsColorPickerOpen(!isColorPickerOpen)}
                            title={t("clickToPickColor")}
                            role="button"
                            tabIndex={0}
                        >
                            <span className="color-code">{values.color || "#000000"}</span>
                        </div>

                        {isColorPickerOpen && (
                            <div className="color-picker-popup">
                                <SketchPicker
                                    color={values.color || "#000000"}
                                    onChange={handleColorChange}
                                    disableAlpha={true}
                                />
                            </div>
                        )}
                    </div>

                    {touched.color && errors.color && (
                        <Alert severity="error" className="form-error-alert">
                            {errors.color}
                        </Alert>
                    )}
                </FormGroup>

                {/* Theme Mode */}
                <FormGroup
                    title={t("themeMode")}
                    subtitle={t("themeModeDescription")}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                >
                    <Field
                        name="themeMode"
                        component={DropdownField}
                        options={[
                            { value: "light", label: t("light") },
                            { value: "dark", label: t("dark") },
                        ]}
                        placeholder={t("selectThemeMode")}
                    />
                    {touched.themeMode && errors.themeMode && (
                        <Alert severity="error" className="form-error-alert">
                            {errors.themeMode}
                        </Alert>
                    )}
                </FormGroup>

                {/* Contact Information */}
                <FormGroup
                    title={t("contactInformation")}
                    subtitle={t("contactInformationDescription")}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                >
                    <Field name="phoneNumber" component={TextField} placeholder={t("enterPhoneNumber")} />
                    {touched.phoneNumber && errors.phoneNumber && (
                        <Alert severity="error" className="form-error-alert">
                            {errors.phoneNumber}
                        </Alert>
                    )}

                    <Field
                        name="emailAddress"
                        component={TextField}
                        placeholder={t("enterEmailAddress")}
                        type="email"
                    />
                    {touched.emailAddress && errors.emailAddress && (
                        <Alert severity="error" className="form-error-alert">
                            {errors.emailAddress}
                        </Alert>
                    )}

                    <Field name="websiteUrl" component={TextField} placeholder={t("enterWebsiteUrl")} />
                    {touched.websiteUrl && errors.websiteUrl && (
                        <Alert severity="error" className="form-error-alert">
                            {errors.websiteUrl}
                        </Alert>
                    )}
                </FormGroup>

                {/* Files */}
                <FormGroup
                    title={t("trainingFiles")}
                    subtitle={`${t("trainingFilesDescription")} ${t("filesWillBeAssociatedWithKnowledgeBase")}`}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                >
                    <div className={`${stepStyles.fileUpload} form-file-upload-constrained`}>
                        <FileInput
                            onFileSelected={handleFilesSelected}
                            showButton={true}
                            buttonVariant="outline"
                            placeholder={t("dragAndDropFilesHereOrClickToSelect")}
                            acceptedFileTypes={CHATBOT_TRAINING_FILE_FORMATS}
                            maxSize={CHATBOT_MAX_FILE_SIZE}
                            multiple={true}
                        />
                        <div className="file-format-info">
                            <Typography variant="caption" color="textSecondary">
                                {t("supportedFormats")}: PDF, HTML, Text (.txt), Markdown (.md) • {t("maxFileSize")}:
                                10MB
                            </Typography>
                        </div>
                    </div>

                    {values.files && values.files.length > 0 && (
                        <div className="files-preview-container">
                            <Typography variant="subtitle2" className={hubStyles.previewLabel}>
                                {t("newFilesToUpload")}:
                            </Typography>
                            <div className="files-list">
                                {values.files.map((file, index) => (
                                    <div key={index} className="file-item">
                                        <span className="file-name">{file.name}</span>
                                        <Tooltip title={t("removeFile")} arrow>
                                            <DeleteIcon
                                                className="database-file-action-icon database-file-delete-icon"
                                                onClick={() => removeFile(index)}
                                            />
                                        </Tooltip>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Existing Files */}
                    {isEdit && botFiles.length > 0 && (
                        <div className="existing-files-container">
                            <Typography variant="subtitle2" className={hubStyles.previewLabel}>
                                {t("existingFiles")} ({t("knowledgeBase")}):
                            </Typography>
                            <div className="files-list">
                                {botFiles.map((file: BotpressFile) => (
                                    <div key={file.id} className="file-item existing-file">
                                        <div className="file-info">
                                            <span className="file-name">{file.key.split("/").pop()}</span>
                                            <span
                                                className="file-status"
                                                title={
                                                    file.status === "upload_pending"
                                                        ? t("uploadPending")
                                                        : file.status === "indexing_completed"
                                                          ? t("indexingCompleted")
                                                          : file.status === "indexing_pending"
                                                            ? t("indexingPending")
                                                            : file.status === "indexing_failed"
                                                              ? `${t("indexingFailed")}: ${file.failedStatusReason || t("unknownError")}`
                                                              : t("fileReady")
                                                }
                                            >
                                                {file.status === "upload_pending"
                                                    ? "⏳"
                                                    : file.status === "indexing_completed"
                                                      ? "✅"
                                                      : file.status === "indexing_pending"
                                                        ? "⏳"
                                                        : file.status === "indexing_failed"
                                                          ? "❌"
                                                          : "📄"}
                                            </span>
                                        </div>
                                        <div className="file-actions">
                                            {file.metadata?.s3Url && (
                                                <button
                                                    type="button"
                                                    onClick={() => onDownloadFile && onDownloadFile(file)}
                                                    className="download-file-button"
                                                    title={t("downloadFile")}
                                                >
                                                    <DownloadIcon className="download-icon" />
                                                </button>
                                            )}
                                            <Tooltip title={t("deleteFile")} arrow>
                                                <DeleteIcon
                                                    className="database-file-action-icon database-file-delete-icon"
                                                    onClick={async () => {
                                                        if (onDeleteFile) {
                                                            try {
                                                                await onDeleteFile(file.id);
                                                            } catch (error) {
                                                                console.error("Error deleting file:", error);
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Tooltip>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {isEdit && isLoadingFiles && (
                        <div className="loading-files">
                            <Typography variant="body2">{t("loadingFiles")}...</Typography>
                        </div>
                    )}
                </FormGroup>

                {/* Action Buttons */}
                <div className={studio.dialogActions}>
                    <ButtonComponent
                        variant="cancel"
                        shape="pill"
                        label={t("cancel")}
                        onClick={onClose}
                        disabled={isSubmitting}
                    />
                    <Tooltip
                        title={
                            Object.keys(errors).length > 0
                                ? t("formIncompleteTooltip", { fields: Object.values(errors).join(", ") })
                                : ""
                        }
                        arrow
                    >
                        <span>
                            <ButtonComponent
                                variant="primary"
                                shape="pill"
                                label={isEdit ? t("updateChatbot") : t("createChatbot")}
                                onClick={submitForm}
                                disabled={isSubmitting || Object.keys(errors).length > 0}
                            />
                        </span>
                    </Tooltip>
                </div>
            </div>
        </Form>
    );
};

const ChatbotList: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { showNotification } = useNotification();
    const { hasAccess, loading: accessLoading, error: accessError } = useAddonAccess("CHATBOT");
    const [bots, setBots] = useState<Bot[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingBot, setEditingBot] = useState<Bot | null>(null);
    const [selectedBotToDelete, setSelectedBotToDelete] = useState<Bot | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [botFiles, setBotFiles] = useState<BotpressFile[]>([]);
    const [isLoadingFiles, setIsLoadingFiles] = useState(false);

    // Validation schema
    const validationSchema = Yup.object({
        name: Yup.string().required(t("nameRequired")),
        description: Yup.string().required(t("descriptionRequired")),
        color: Yup.string().matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, t("invalidColorFormat")),
        themeMode: Yup.string().oneOf(["light", "dark"]).required(),
    });

    // Initial values for forms - must be before any conditional logic
    const initialValues: ChatbotFormValues = useMemo(() => {
        // Helper function to find webchat configuration (defined inline to avoid hoisting issues)
        const getWebchatConfiguration = (bot: Bot | null) => {
            if (!bot?.integrations) return null;
            const webchatIntegration = Object.values(bot.integrations).find(
                (integration) => integration.name === "webchat",
            );
            return webchatIntegration?.configuration || null;
        };

        // Helper function to map technical API values to user-friendly form values
        const mapApiToForm = (webchatConfig: any): Partial<ChatbotFormValues> => ({
            name: webchatConfig?.botDisplayName || "",
            description: webchatConfig?.botDescription || "",
            logo: webchatConfig?.botAvatarUrl || undefined,
            color: webchatConfig?.primaryColor || "#000000",
            themeMode: webchatConfig?.themeMode || "light",
            phoneNumber: webchatConfig?.descriptionPhoneNumber?.link || "",
            emailAddress: webchatConfig?.descriptionEmailAddress?.link || "",
            websiteUrl: webchatConfig?.descriptionWebsiteUrl?.link || "",
        });

        const webchatConfig = getWebchatConfiguration(editingBot);
        const mappedValues = mapApiToForm(webchatConfig);
        return {
            name: mappedValues.name || editingBot?.name || "",
            description: mappedValues.description || "",
            logo: mappedValues.logo || undefined,
            color: mappedValues.color || "#000000",
            themeMode: mappedValues.themeMode || "light",
            phoneNumber: mappedValues.phoneNumber || "",
            emailAddress: mappedValues.emailAddress || "",
            websiteUrl: mappedValues.websiteUrl || "",
            files: [],
        };
    }, [editingBot]);

    // Fetch bots on component mount only if user has access
    useEffect(() => {
        if (hasAccess && !accessLoading) {
            fetchBots();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasAccess, accessLoading]);

    // Early return for access control
    if (accessLoading) {
        return (
            <div className="form-loading-container">
                <div className="form-loading-spinner"></div>
                <p className="form-loading-text">{t("loading")}</p>
            </div>
        );
    }

    if (!hasAccess) {
        return <AccessDenied featureName={t("chatbots")} addonName={t("addon.chatbot")} />;
    }

    if (accessError) {
        return (
            <div className="form-loading-container">
                <Alert severity="error">{accessError}</Alert>
            </div>
        );
    }

    const fetchBots = async () => {
        try {
            setLoading(true);
            const response = await listBots();
            if (response) {
                setBots(response.bots);
            }
        } catch (error) {
            console.error("Error fetching bots:", error);
            showNotification({
                message: t("errorFetchingChatbots"),
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreateBot = async (values: ChatbotFormValues, formikHelpers: FormikHelpers<ChatbotFormValues>) => {
        try {
            // Create the bot first
            const botData: BotCreateRequest = {
                name: values.name,
                dev: false,
            };
            const newBot = await createBot(botData);

            // Upload files if any
            let uploadedFileNames: string[] = [];
            if (values.files.length > 0) {
                uploadedFileNames = await uploadFilesToBotpress(newBot.id, values.files);
            }

            // Update webchat configuration using mapping function
            const webchatConfig = mapFormToApi(values);
            // Add uploaded file names to the configuration
            webchatConfig.trainingFiles = uploadedFileNames;

            // Check if webchat integration exists, if not create it
            if (hasWebchatIntegration(newBot)) {
                const webchatIntegrationId = getWebchatIntegrationId(newBot);
                await updateWebchatConfiguration(newBot.id, webchatConfig, webchatIntegrationId || undefined);
            } else {
                // Create webchat integration with configuration
                await createOrUpdateBotWithWebchat(newBot.id, {}, webchatConfig);
            }

            showNotification({
                message: t("chatbotCreatedSuccessfully"),
                type: "success",
            });
            setModalOpen(false);
            fetchBots();
            formikHelpers.resetForm();
        } catch (error) {
            console.error("Error creating bot:", error);
            showNotification({
                message: t("errorCreatingChatbot"),
                type: "error",
            });
        }
    };

    const handleUpdateBot = async (values: ChatbotFormValues, formikHelpers: FormikHelpers<ChatbotFormValues>) => {
        if (!editingBot) return;

        try {
            // Upload new files if any
            let uploadedFileNames: string[] = [];
            if (values.files.length > 0) {
                uploadedFileNames = await uploadFilesToBotpress(editingBot.id, values.files);
            }

            // Update webchat configuration using mapping function
            const webchatConfig = mapFormToApi(values);
            // Add uploaded file names to the configuration
            if (uploadedFileNames.length > 0) {
                webchatConfig.trainingFiles = uploadedFileNames;
            }

            // First, let's try updating just the bot name to see if that works
            try {
                await updateBot(editingBot.id, { name: values.name });
                console.log("Bot name updated successfully");
            } catch (nameError) {
                console.error("Error updating bot name:", nameError);
            }

            // Then try to update webchat configuration
            const webchatIntegrationId = getWebchatIntegrationId(editingBot);
            if (webchatIntegrationId) {
                try {
                    await createOrUpdateBotWithWebchat(editingBot.id, {}, webchatConfig, webchatIntegrationId);
                    console.log("Webchat configuration updated successfully");
                } catch (webchatError) {
                    console.error("Error updating webchat configuration:", webchatError);
                }
            } else {
                console.log("No webchat integration found, skipping webchat configuration update");
            }

            showNotification({
                message: t("chatbotUpdatedSuccessfully"),
                type: "success",
            });
            setModalOpen(false);
            setEditingBot(null);
            fetchBots();
            formikHelpers.resetForm();
        } catch (error) {
            console.error("Error updating bot:", error);
            showNotification({
                message: t("errorUpdatingChatbot"),
                type: "error",
            });
        }
    };

    const handleDeleteBot = async (botId: string) => {
        const botToDelete = bots.find((b) => b.id === botId);
        if (!botToDelete) return;

        // Optimistic update: immediately remove from UI
        setBots((prevBots) => prevBots.filter((b) => b.id !== botId));

        try {
            await deleteBot(botId);
            showNotification({
                message: t("chatbotDeletedSuccessfully"),
                type: "success",
            });
        } catch (error) {
            console.error("Error deleting bot:", error);
            showNotification({
                message: t("errorDeletingChatbot"),
                type: "error",
            });

            // Revert the optimistic update on error
            setBots((prevBots) => [...prevBots, botToDelete]);
        }
    };

    const openEditModal = async (bot: Bot) => {
        try {
            // Fetch the full bot data to get the complete webchat configuration
            const response = await getBot(bot.id);
            console.log("fullBot", response);
            // The API returns { bot: { ... } }, so we need to extract the bot object
            const fullBot = response.bot || response;
            console.log("Bot integrations:", fullBot.integrations);
            console.log("Available integration keys:", Object.keys(fullBot.integrations || {}));
            console.log(
                "Integration details:",
                Object.entries(fullBot.integrations || {}).map(([id, integration]) => ({
                    id,
                    name: integration.name,
                    enabled: integration.enabled,
                    configuration: integration.configuration,
                })),
            );

            // Log the webchat configuration specifically
            const webchatIntegration = Object.values(fullBot.integrations || {}).find(
                (integration) => integration.name === "webchat",
            );
            if (webchatIntegration) {
                console.log("Webchat configuration details:", webchatIntegration.configuration);
            }

            setEditingBot(fullBot);

            // Load existing files for this bot
            await loadBotFiles(bot.id);

            setModalOpen(true);
        } catch (error) {
            console.error("Error fetching bot details:", error);
            showNotification({
                message: t("errorFetchingBotDetails"),
                type: "error",
            });
        }
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingBot(null);
    };

    const handleViewBot = (bot: Bot) => {
        if (bot.url) {
            window.open(bot.url, "_blank");
        }
    };

    // Helper function to find webchat configuration
    const getWebchatConfiguration = (bot: Bot | null) => {
        if (!bot?.integrations) return null;

        // Find the webchat integration by name
        const webchatIntegration = Object.values(bot.integrations).find(
            (integration) => integration.name === "webchat",
        );

        return webchatIntegration?.configuration || null;
    };

    // Helper function to find webchat integration ID
    const getWebchatIntegrationId = (bot: Bot | null) => {
        if (!bot?.integrations) return null;

        // Find the webchat integration by name and return its ID
        const webchatIntegrationEntry = Object.entries(bot.integrations).find(
            ([id, integration]) => integration.name === "webchat" || integration.name === "webchat-v2",
        );

        if (webchatIntegrationEntry) {
            console.log("Found webchat integration:", webchatIntegrationEntry[0], webchatIntegrationEntry[1]);
            return webchatIntegrationEntry[0];
        }

        // If not found by name, log all integrations for debugging
        console.log(
            "All integrations:",
            Object.entries(bot.integrations).map(([id, integration]) => ({
                id,
                name: integration.name,
                enabled: integration.enabled,
            })),
        );

        return null;
    };

    // Helper function to check if webchat integration exists
    const hasWebchatIntegration = (bot: Bot | null) => {
        return getWebchatIntegrationId(bot) !== null;
    };

    // Helper function to map user-friendly form values to technical API values
    const mapFormToApi = (values: ChatbotFormValues): WebchatConfiguration => ({
        botDisplayName: values.name,
        botDescription: values.description,
        botAvatarUrl: values.logo,
        primaryColor: values.color,
        themeMode: values.themeMode,
        descriptionPhoneNumber: {
            title: "Phone",
            link: values.phoneNumber,
        },
        descriptionEmailAddress: {
            title: "Email",
            link: values.emailAddress,
        },
        descriptionWebsiteUrl: {
            title: "Website",
            link: values.websiteUrl,
        },
        trainingFiles: values.files.map((file) => file.name),
    });

    /**
     * Loads files for a specific bot using the knowledge base tag filter
     * Only returns files that have both 'chatbot:{botId}' and 'kbId:kb-default' tags
     * This ensures we only show files that belong to this specific chatbot and knowledge base
     */
    const loadBotFiles = async (botId: string) => {
        setIsLoadingFiles(true);
        try {
            // Load files filtered by both chatbot ID and knowledge base using the new API function
            const response = await listFilesByKnowledgeBase(botId, "kb-default");
            setBotFiles(response.files);
        } catch (error) {
            console.error("Error loading bot files:", error);
            showNotification({
                message: t("errorLoadingFiles"),
                type: "error",
            });
        } finally {
            setIsLoadingFiles(false);
        }
    };

    /**
     * Downloads a file from S3 URL stored in metadata
     * Creates a temporary link and triggers download
     */
    const handleDownloadFile = (file: BotpressFile) => {
        try {
            // Get S3 URL from metadata
            const s3Url = file.metadata?.s3Url;
            if (!s3Url) {
                showNotification({
                    message: t("noDownloadUrlAvailable"),
                    type: "error",
                });
                return;
            }

            // Create a temporary link and trigger download
            const link = document.createElement("a");
            link.href = s3Url;
            link.download = file.metadata?.originalName || file.key.split("/").pop() || "download";
            link.target = "_blank";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            showNotification({
                message: t("fileDownloadStarted"),
                type: "success",
            });
        } catch (error) {
            console.error("Error downloading file:", error);
            showNotification({
                message: t("errorDownloadingFile"),
                type: "error",
            });
        }
    };

    /**
     * Uploads files using the S3 -> Backend -> Botpress flow
     * This approach uses the backend to handle the Botpress upload, keeping API keys secure
     * Files are tagged with 'chatbot:{botId}' and 'kbId:kb-default' for proper knowledge base association
     * All files are automatically indexed for semantic search
     */
    const uploadFilesToBotpress = async (botId: string, files: File[]): Promise<string[]> => {
        const uploadedFileNames: string[] = [];

        for (const file of files) {
            try {
                // Step 1: Upload file to S3 for permanent storage
                const token = localStorage.getItem("token");
                if (!token) {
                    throw new Error("No authentication token found");
                }

                const s3Url = await uploadFileToS3Complete(
                    {
                        file,
                        isPublic: true, // Public files for general uploads
                        isFolder: false, // No folder structure for general uploads
                    },
                    token,
                );

                // Step 2: Send S3 URL to backend for Botpress upload
                await uploadFileFromUrl(botId, {
                    key: `kb-default/${Date.now()}_${file.name}`, // Use kb-default prefix for knowledge base
                    url: s3Url,
                    size: file.size,
                    tags: {
                        chatbot: botId, // Tag to identify files belonging to this chatbot
                        kbId: "kb-default", // Associate with the default knowledge base
                        source: "knowledge-base", // Indicate this is knowledge base content
                        category: "training",
                        uploadedAt: new Date().toISOString(),
                        title: file.name, // Add title for better organization
                    },
                    accessPolicies: ["integrations"], // Allow integrations to access the file
                    contentType: file.type,
                });

                console.log(`Successfully uploaded ${file.name} to knowledge base for bot ${botId}`);
                uploadedFileNames.push(file.name);
            } catch (error) {
                console.error(`Error uploading file ${file.name}:`, error);
                showNotification({
                    message: t("errorUploadingFile", { fileName: file.name }),
                    type: "error",
                });
            }
        }

        return uploadedFileNames;
    };

    // Generate breadcrumbs
    const breadcrumbItems = [
        { label: t("home"), href: "/" },
        { label: t("chatbots"), href: "/chatbots" },
    ];

    if (loading) {
        return (
            <div className="form-loading-container">
                <div className="form-loading-spinner"></div>
                <p className="form-loading-text">{t("loadingChatbots")}</p>
            </div>
        );
    }

    return (
        <div className="hub-list-container">
            <div className={pageHeader.withBreadcrumb}>
                <Breadcrumb items={breadcrumbItems} />
                <div className={pageHeader.titleSection}>
                    <div className={pageHeader.content}>
                        <h2 className="settings-title">{t("myChatbotsTitle")}</h2>
                        <div className="page-subtitle-with-info">
                            <Typography variant="body2" className={pageHeader.subtitle}>
                                {t("myChatbotsDescription")}
                            </Typography>
                            <Tooltip title={t("chatbotExplanationTooltip")} arrow placement="top">
                                <InformationIcon className={hubStyles.infoIcon} />
                            </Tooltip>
                        </div>
                    </div>
                    {/* <AddButton
            onClick={openCreateModal}
            tooltip={t("createChatbot")}
          /> */}
                </div>
            </div>

            {bots.length === 0 ? (
                <div className={hubStyles.emptyState}>
                    <div className={hubStyles.emptyStateIcon}>🤖</div>
                    <Typography variant="h6" className={hubStyles.emptyStateTitle}>
                        {t("noChatbotsYet")}
                    </Typography>
                    <Typography variant="body2" className={hubStyles.emptyStateText}>
                        {t("noChatbotsDescription")}
                    </Typography>
                    {/* <AddButton
            onClick={openCreateModal}
            tooltip={t("createChatbot")}
          /> */}
                </div>
            ) : (
                <div className={hubStyles.hubsGrid}>
                    {bots.map((bot) => (
                        <Paper key={bot.id} elevation={2} className="hub-card">
                            <div className="hub-card-content">
                                {/* Bot Info */}
                                <div className="hub-info">
                                    <div className="hub-name-status-row">
                                        <Typography variant="h6" className="hub-name">
                                            {getWebchatConfiguration(bot)?.botDisplayName || bot.name}
                                        </Typography>
                                        <div className="bot-status">
                                            <span className="status-label">
                                                {bot.dev ? t("development") : t("production")}
                                            </span>
                                        </div>
                                    </div>
                                    {getWebchatConfiguration(bot)?.botDescription && (
                                        <Typography variant="body2" className="bot-description">
                                            {getWebchatConfiguration(bot)?.botDescription}
                                        </Typography>
                                    )}
                                </div>

                                {/* Logo and Color */}
                                <div className="hub-branding">
                                    {getWebchatConfiguration(bot)?.primaryColor && (
                                        <div className="hub-color-container">
                                            <div>{t("Color")}:</div>
                                            <div
                                                className="hub-color-indicator"
                                                style={{ backgroundColor: getWebchatConfiguration(bot)?.primaryColor }}
                                                title={getWebchatConfiguration(bot)?.primaryColor}
                                            />
                                        </div>
                                    )}
                                    {getWebchatConfiguration(bot)?.botAvatarUrl && (
                                        <div className="hub-logo-container">
                                            <img
                                                src={getWebchatConfiguration(bot)?.botAvatarUrl}
                                                alt="Bot Logo"
                                                className="hub-logo"
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Bot Actions */}
                                <div className="hub-card-actions">
                                    <div className="hub-action-buttons">
                                        {bot.url && (
                                            <Tooltip title={t("viewChatbot")}>
                                                <EyeIcon
                                                    className="settings-icon-button"
                                                    style={{ color: "#ff9800" }}
                                                    onClick={() => handleViewBot(bot)}
                                                />
                                            </Tooltip>
                                        )}
                                        <Tooltip title={t("edit")} arrow>
                                            <EditIcon
                                                className="settings-icon-button"
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    openEditModal(bot);
                                                }}
                                            />
                                        </Tooltip>
                                        {/* <Tooltip title={t("delete")} arrow>
                      <DeleteIcon
                        className="delete-icon-button"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setSelectedBotToDelete(bot);
                          setIsDeleteModalOpen(true);
                        }}
                      />
                    </Tooltip> */}
                                    </div>
                                </div>
                            </div>
                        </Paper>
                    ))}
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {selectedBotToDelete && (
                <DeleteModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => {
                        setIsDeleteModalOpen(false);
                        setSelectedBotToDelete(null);
                    }}
                    onConfirm={() => {
                        handleDeleteBot(selectedBotToDelete.id);
                        setSelectedBotToDelete(null);
                    }}
                    message={t("deleteChatbotConfirmation")}
                    title={t("deleteChatbot")}
                />
            )}

            {/* Create/Edit Modal */}
            <Modal open={modalOpen} onClose={closeModal} className={hubStyles.hubModalOverlay}>
                <Paper className={hubStyles.hubModalContent}>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={editingBot ? handleUpdateBot : handleCreateBot}
                        enableReinitialize
                        validateOnMount
                    >
                        <ChatbotForm
                            onClose={closeModal}
                            isEdit={!!editingBot}
                            originalBot={editingBot}
                            botFiles={botFiles}
                            isLoadingFiles={isLoadingFiles}
                            onDeleteFile={async (fileId: string) => {
                                if (editingBot) {
                                    try {
                                        await deleteFile(editingBot.id, fileId);
                                        await loadBotFiles(editingBot.id);
                                        showNotification({
                                            message: t("fileDeletedSuccessfully"),
                                            type: "success",
                                        });
                                    } catch (error) {
                                        console.error("Error deleting file:", error);
                                        showNotification({
                                            message: t("errorDeletingFile"),
                                            type: "error",
                                        });
                                    }
                                }
                            }}
                            onLoadBotFiles={loadBotFiles}
                            onDownloadFile={handleDownloadFile}
                        />
                    </Formik>
                </Paper>
            </Modal>
        </div>
    );
};

export default ChatbotList;
